import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractDialogResultData, AbstractFormDialog } from '@next/core-lib/dialog';
import { SiteState } from '@next/core-lib/site';
import { select, Store } from '@ngrx/store';
import { delay, distinctUntilChanged, filter, startWith, take } from 'rxjs/operators';
import { Crop } from 'src/app/shared/model';
import { getCrops } from 'src/app/store/crops/crops.selector';

import { CropSetting, CropSettingsDialogModel } from '../../cropsetting.model';

@Component({
  selector: 'app-cropsetting-dialog',
  templateUrl: './cropsetting-dialog.component.html',
  standalone: false,
})
export class CropSettingsDialogComponent extends AbstractFormDialog<CropSetting> {
  data = inject<CropSettingsDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<MatDialogRef<CropSettingsDialogComponent, AbstractDialogResultData<CropSetting>>>(
      MatDialogRef,
    );
  private readonly fb = inject(FormBuilder);
  private readonly store = inject<Store<SiteState>>(Store);

  currentYear = new Date().getFullYear();
  availableCropYears = Array.from({ length: this.currentYear + 2 - 2020 }, (_, k) => k + 2020);
  formGroup = this.fb.group({
    cropId: [
      {
        value: this.data.cropSetting.cropId ?? 0,
        disabled: !this.isNewCropSetting(),
      },
      [Validators.required, Validators.maxLength(50)],
    ],
    calendarYear: [this.data.cropSetting.calendarYear ?? this.currentYear, [Validators.required]],
    cultivationMonthsFrom: [
      this.data.cropSetting.cultivationMonthsFrom,
      [Validators.required, Validators.maxLength(50)],
    ],
    cultivationMonthsUntil: [
      this.data.cropSetting.cultivationMonthsUntil,
      [Validators.required, Validators.maxLength(50)],
    ],
    norm: [this.data.cropSetting.norm, [Validators.required, Validators.maxLength(50)]],
  });
  crops = toSignal(this.store.pipe(select(getCrops), take(1)), { initialValue: [] as Crop[] });

  private readonly cropIdValue = toSignal(
    this.formGroup.controls.cropId.valueChanges.pipe(
      startWith(''),
      distinctUntilChanged(),
      delay(100),
      filter((value) => typeof value === 'string'),
    ),
    { initialValue: '' },
  );

  filteredCrops = computed(() => {
    const filterValue = (this.cropIdValue() || '').toString().toLowerCase();
    return this.crops()
      .filter((option) => option.agrifirmCrop.name.toLowerCase().includes(filterValue))
      .sort((a, b) =>
        a.agrifirmCrop.name.toUpperCase() > b.agrifirmCrop.name.toUpperCase() ? 1 : -1,
      );
  });

  isNewCropSetting() {
    return !this.data.cropSetting.cropId;
  }

  confirm() {
    const result = {
      ...this.data.cropSetting,
      ...this.formGroup.getRawValue(),
    };
    result.cropName = this.findCrop(result.cropId)?.agrifirmCrop?.name;
    if (this.formGroup.valid && result.cropId) {
      super.confirm(result);
    }
  }

  displayCrop() {
    return (selectedCropId?: number) =>
      selectedCropId ? this.findCrop(selectedCropId)?.agrifirmCrop?.name : '';
  }

  findCrop(selectedCropId: number): Crop {
    return this.crops().find((crop) => crop.id === selectedCropId);
  }
}
